//libs/investigator/utilities/src/lib/setHumioSessionId.ts
import axios from 'axios';

/**
 * Configuration for Humio token generation
 */
interface HumioTokenConfig {
  idToken: string;
  tenantType: 'STANDARD' | 'POV' | 'PRIME';
  tenantId?: string;
  userId?: string;
}

/**
 * Response structure for Humio token
 */
interface HumioTokenResponse {
  token: string;
  expiresAt?: number;
}

/**
 * Generates user-scoped localStorage keys
 * @param {string} baseKey - Base key name
 * @param {string} userId - Optional user ID for scoping
 * @returns {string} Scoped key name
 */
const getUserScopedKey = (baseKey: string, userId?: string): string => {
  if (!userId) return baseKey;
  return `${baseKey}_${userId}`;
};

/**
 * Singleton class managing Humio token generation and caching.
 * Ensures only one token is generated per session with proper rate limiting.
 */
class HumioTokenManager {
  private static instance: HumioTokenManager;
  private tokenCache: Map<string, HumioTokenResponse> = new Map();
  private tokenGenerationInProgress: Map<string, Promise<string>> = new Map();
  private readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
  private tokenGenerationAttempts: Map<string, number> = new Map();
  private lastTokenGenerationTime: Map<string, number> = new Map();

  /**
   * Gets the singleton instance of HumioTokenManager
   * @returns {HumioTokenManager} The singleton instance
   */
  static getInstance(): HumioTokenManager {
    if (!HumioTokenManager.instance) {
      HumioTokenManager.instance = new HumioTokenManager();
    }
    return HumioTokenManager.instance;
  }

  /**
   * Gets or generates a Humio session token with caching and rate limiting.
   * Ensures only one token generation process occurs at a time.
   *
   * @param {HumioTokenConfig} config - Token configuration
   * @param {boolean} forceRefresh - Force a new token generation
   * @returns {Promise<string>} The Humio session token
   * @throws {Error} If token generation fails or rate limit is exceeded
   */
  async getToken(
    config: HumioTokenConfig,
    forceRefresh = false,
  ): Promise<string> {
    const cacheKey = this.getCacheKey(config);

    // Rate limiting check
    const lastAttempt = this.lastTokenGenerationTime.get(cacheKey) || 0;
    const timeSinceLastAttempt = Date.now() - lastAttempt;
    const attempts = this.tokenGenerationAttempts.get(cacheKey) || 0;

    if (attempts > 5 && timeSinceLastAttempt < 10000) {
      throw new Error(
        'Too many token generation attempts. Please wait before retrying.',
      );
    }

    // Clear cache if force refresh requested
    if (forceRefresh) {
      this.tokenCache.delete(cacheKey);
      this.tokenGenerationInProgress.delete(cacheKey);
    }

    // Check for cached token
    const cached = this.tokenCache.get(cacheKey);
    if (cached && this.isTokenValid(cached) && !forceRefresh) {
      return cached.token;
    }

    // Check if token generation is already in progress
    const inProgress = this.tokenGenerationInProgress.get(cacheKey);
    if (inProgress) {
      return inProgress;
    }

    // Start new token generation
    const tokenPromise = this.generateToken(config);
    this.tokenGenerationInProgress.set(cacheKey, tokenPromise);

    // Track attempt
    this.tokenGenerationAttempts.set(cacheKey, attempts + 1);
    this.lastTokenGenerationTime.set(cacheKey, Date.now());

    try {
      const token = await tokenPromise;

      // Cache the token
      this.tokenCache.set(cacheKey, {
        token,
        expiresAt: Date.now() + this.CACHE_DURATION,
      });

      // Persist to localStorage
      this.persistToken(config, token);

      // Reset attempts on success
      this.tokenGenerationAttempts.set(cacheKey, 0);

      return token;
    } finally {
      // Clean up in-progress tracking
      this.tokenGenerationInProgress.delete(cacheKey);
    }
  }

  /**
   * Generates a new Humio session token via API call
   * @private
   * @param {HumioTokenConfig} config - Token configuration
   * @returns {Promise<string>} The generated token
   * @throws {Error} If API call fails or returns invalid token
   */
  private async generateToken(config: HumioTokenConfig): Promise<string> {
    const url = this.getTokenEndpoint(config);

    try {
      // Attempt axios request first (better error handling)
      const response = await axios.post(
        url,
        {},
        {
          headers: {
            Authorization: `Bearer ${config.idToken}`,
            'Content-Type': 'application/json',
          },
          timeout: 10000,
        },
      );

      // Extract token from various possible response formats
      const sessionToken =
        response.data?.token ||
        response.data?.client_session_id ||
        response.data?.clientSessionId ||
        response.data?.sessionToken;

      if (!sessionToken || sessionToken === 'undefined') {
        throw new Error(`Invalid session token received from ${url}`);
      }

      return sessionToken;
    } catch (error: unknown) {
      // TODO: Create a proper type for the response
      type TokenError = { message: string; code?: string };
      // Fallback to fetch API for CORS issues
      if (
        (error as TokenError).message?.includes('Network Error') ||
        (error as TokenError).code === 'ERR_NETWORK'
      ) {
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${config.idToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({}),
          });

          if (!response.ok) {
            throw new Error(
              `HTTP ${response.status}: ${response.statusText} for ${url}`,
            );
          }

          const data = await response.json();
          const sessionToken =
            data?.token ||
            data?.client_session_id ||
            data?.clientSessionId ||
            data?.sessionToken;

          if (!sessionToken || sessionToken === 'undefined') {
            throw new Error(`Invalid session token received from ${url}`);
          }

          return sessionToken;
        } catch (fetchError: unknown) {
          throw new Error(`Failed to generate Humio token: ${fetchError}`);
        }
      }

      throw new Error(
        `Failed to generate Humio token: ${(error as TokenError).message}`,
      );
    }
  }

  /**
   * Gets the appropriate token endpoint based on tenant type
   * @private
   * @param {HumioTokenConfig} config - Token configuration
   * @returns {string} The token endpoint URL
   * @throws {Error} If PRIME tenant lacks tenantId
   */
  private getTokenEndpoint(config: HumioTokenConfig): string {
    let endpoint: string;

    switch (config.tenantType) {
      case 'PRIME':
        if (!config.tenantId) {
          throw new Error('PRIME tenant requires tenantId');
        }
        endpoint =
          process.env.NX_PUBLIC_PRIME_HUMIO_SESSION_TOKEN_URL?.replace(
            '{tenantId}',
            config.tenantId,
          ) || `/search-${config.tenantId}/api/v1/authenticate/token`;
        break;

      case 'POV':
        endpoint =
          process.env.NX_PUBLIC_POV_HUMIO_SESSION_TOKEN_URL ||
          '/search-internal/api/v1/authenticate/token';
        break;

      case 'STANDARD':
      default:
        endpoint =
          process.env.NX_PUBLIC_HUMIO_SESSION_TOKEN_URL ||
          '/search/api/v1/authenticate/token';
        break;
    }

    return endpoint;
  }

  /**
   * Generates a cache key for token storage
   * @private
   * @param {HumioTokenConfig} config - Token configuration
   * @returns {string} The cache key
   */
  private getCacheKey(config: HumioTokenConfig): string {
    const userPart = config.userId ? `-${config.userId}` : '';
    return `${config.tenantType}-${config.tenantId || 'default'}${userPart}`;
  }

  /**
   * Checks if a cached token is still valid
   * @private
   * @param {HumioTokenResponse} cached - Cached token response
   * @returns {boolean} True if token is valid
   */
  private isTokenValid(cached: HumioTokenResponse): boolean {
    if (
      !cached.token ||
      cached.token === 'undefined' ||
      cached.token === 'null'
    ) {
      return false;
    }
    if (!cached.expiresAt) {
      return true;
    }
    return Date.now() < cached.expiresAt;
  }

  /**
   * Persists token to localStorage for recovery across sessions
   * @private
   * @param {HumioTokenConfig} config - Token configuration
   * @param {string} token - Token to persist
   */
  private persistToken(config: HumioTokenConfig, token: string): void {
    const storageKey = `humio_token_${this.getCacheKey(config)}`;
    localStorage.setItem(storageKey, token);

    // Store with user-scoped key if userId provided
    if (config.userId) {
      const userTokenKey = getUserScopedKey('session_id_token', config.userId);
      localStorage.setItem(userTokenKey, token);
    }

    // Legacy key for backward compatibility
    localStorage.setItem('session_id_token', token);
  }

  /**
   * Clears all cached tokens
   * @param {string} userId - Optional user ID to clear only user-specific tokens
   */
  clearTokens(userId?: string): void {
    if (userId) {
      // Clear user-specific entries
      for (const [key] of this.tokenCache.entries()) {
        if (key.includes(userId)) {
          this.tokenCache.delete(key);
        }
      }
      for (const [key] of this.tokenGenerationInProgress.entries()) {
        if (key.includes(userId)) {
          this.tokenGenerationInProgress.delete(key);
        }
      }
      for (const [key] of this.tokenGenerationAttempts.entries()) {
        if (key.includes(userId)) {
          this.tokenGenerationAttempts.delete(key);
        }
      }
      for (const [key] of this.lastTokenGenerationTime.entries()) {
        if (key.includes(userId)) {
          this.lastTokenGenerationTime.delete(key);
        }
      }
    } else {
      // Clear all caches
      this.tokenCache.clear();
      this.tokenGenerationInProgress.clear();
      this.tokenGenerationAttempts.clear();
      this.lastTokenGenerationTime.clear();
    }

    // Clear from localStorage
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (userId) {
        if (
          key.includes(userId) &&
          (key.startsWith('humio_token_') ||
            key.startsWith('session_id_token_'))
        ) {
          localStorage.removeItem(key);
        }
      } else {
        if (key.startsWith('humio_token_') || key === 'session_id_token') {
          localStorage.removeItem(key);
        }
      }
    });

    if (userId) {
      const userTokenKey = getUserScopedKey('session_id_token', userId);
      localStorage.removeItem(userTokenKey);
    }

    if (!userId) {
      localStorage.removeItem('session_id_token');
    }
  }

  /**
   * Loads a persisted token from localStorage if available
   * @param {HumioTokenConfig} config - Token configuration
   * @returns {string | null} The loaded token or null if not found
   */
  loadPersistedToken(config: HumioTokenConfig): string | null {
    const storageKey = `humio_token_${this.getCacheKey(config)}`;
    const token = localStorage.getItem(storageKey);

    if (token && token !== 'undefined' && token !== 'null') {
      // Re-cache the token
      this.tokenCache.set(this.getCacheKey(config), {
        token,
        expiresAt: Date.now() + this.CACHE_DURATION,
      });
      return token;
    }

    // Try user-scoped key as fallback
    if (config.userId) {
      const userToken = localStorage.getItem(
        getUserScopedKey('session_id_token', config.userId),
      );

      if (userToken && userToken !== 'undefined' && userToken !== 'null') {
        this.tokenCache.set(this.getCacheKey(config), {
          token: userToken,
          expiresAt: Date.now() + this.CACHE_DURATION,
        });
        return userToken;
      }
    }

    return null;
  }
}

// Export singleton instance
const humioTokenManager = HumioTokenManager.getInstance();

/**
 * Main function for obtaining and caching a Humio session token.
 * Provides backward compatibility while delegating to HumioTokenManager.
 *
 * @param {Object} params - Parameters for session token retrieval
 * @param {string} params.idToken - The user's ID token for authentication
 * @param {string} params.humioUrlPrefix - The URL prefix for the Humio instance
 * @param {boolean} params.forceRefresh - Force a new token request, bypassing cache
 * @param {string} params.userId - Optional user ID for scoped storage
 * @returns {Promise<string>} The Humio session token
 * @throws {Error} If unable to obtain a valid session token
 */
export const setHumioSessionId = async ({
  idToken,
  humioUrlPrefix,
  forceRefresh = false,
  userId,
}: {
  idToken: string;
  humioUrlPrefix: string;
  forceRefresh?: boolean;
  userId?: string;
}): Promise<string> => {
  // Determine tenant type from URL prefix
  let tenantType: 'STANDARD' | 'POV' | 'PRIME' = 'STANDARD';
  let tenantId: string | undefined;

  if (humioUrlPrefix === 'search-internal') {
    tenantType = 'POV';
  } else if (humioUrlPrefix.startsWith('search-')) {
    tenantType = 'PRIME';
    tenantId = humioUrlPrefix.replace('search-', '');
  }

  return humioTokenManager.getToken(
    {
      idToken,
      tenantType,
      tenantId,
      userId,
    },
    forceRefresh,
  );
};

/**
 * Clears all cached Humio session data.
 * Should be called on user logout.
 *
 * @param {string} userId - Optional user ID to clear only user-specific data
 */
export const clearHumioSessionCache = (userId?: string) => {
  humioTokenManager.clearTokens(userId);
};

// Export the manager for direct use if needed
export { humioTokenManager };
